import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { GuestScreeningService } from '../../screening/guest-screening.service';
import { CommonModule } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { RouterModule } from '@angular/router';
import { MatOption } from '@angular/material/core';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { map, Observable, startWith } from 'rxjs';
import { TermsAndConditionsComponent } from '../terms-and-conditions/terms-and-conditions.component';
import { Organization } from '../../../../organizations/models/Organization';
import { OrganizationService } from '../../../../organizations/services/organizations.service';
import { VerificationPackagesService } from '../../../../verification-packages/services/verification-packages.service';
import { NotificationService } from '../../../../../utilities/PortalNotificationUtility';

@Component({
  selector: 'app-guest-screening-form',
  templateUrl: './guest-screening-form.component.html',
  styleUrls: ['./guest-screening-form.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinner,
    TermsAndConditionsComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatOption,
    MatSelect,
    MatInputModule,
    MatButtonModule,
    MatExpansionModule,
    MatIconModule,
    MatAutocompleteModule,
    RouterModule
  ]
})
export class GuestScreeningFormComponent implements OnInit, AfterViewInit {

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  @Input() guestScreeningService!: GuestScreeningService;
  @Input() guestForm!: FormGroup;

  @Output() middleNameToggled: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() phoneNumberChanged: EventEmitter<Event> = new EventEmitter<Event>();

  organizations!: any[];
  orgControl = new FormControl('', [Validators.required, this.requireMatch.bind(this)]);
  filteredOrganizations!: Observable<any[]>;
  organizationSelect!: Organization | undefined;
  organizationSelectId: number = 0;
  orgVerifPackages: any;
  guestFormInvalid: boolean = false;

  constructor(
    private organizationService: OrganizationService,
    private verificationPackageService: VerificationPackagesService,
    private notificationService: NotificationService
  ) {}

  ngOnInit() {
    this.getOrganizations();
  }

  ngAfterViewInit() {
    this.autocompleteTrigger.panelClosingActions.subscribe((e) => {      
      if (!(e && e.source)) {
        this.orgControl.setValue('');
        this.autocompleteTrigger.closePanel();
      }
    });
  }

  async getOrganizations() {
    const organizations = await this.organizationService.getOrganizations();
    this.organizations = organizations.filter(org => org.for_guest_verification === 1);
    this.filteredOrganizations = this.orgControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || '')),
    );
  }

  private _filter(value: string): string[] {
    if (value.length > 0) {
      return this.organizations.filter(org => org.organization_name.toLowerCase().includes(value.toLowerCase()));
    } else {
      return this.organizations;
    }
  }

  private requireMatch(control: FormControl): ValidationErrors | null {
    const selection: any = control.value;
    if (this.organizations && this.organizations.indexOf(selection) < 0) {
      return { requireMatch: true };
    }
    return null;
  }

  async onOrgSelectionChanged(event: MatAutocompleteSelectedEvent) {
    this.organizationSelect = event.option.value;
    this.guestScreeningService.organizationId = this.organizationSelect?.organization_id;

    if (this.organizationSelect?.organization_id) {
      this.organizationSelectId = this.organizationSelect?.organization_id;
    }

    this.orgVerifPackages = await this.verificationPackageService.getVerificationPackagesByOrgId(this.organizationSelect?.organization_id);
    if (this.orgVerifPackages.length === 0) {
      this.notificationService.showNotification({ message: 'Organization has no verification package assigned' });
      this.guestFormInvalid = true;
    }
  }

  displayOrgFn(org: Organization): string {
    return org && org.organization_name ? org.organization_name : '';
  }
}